import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaGoogle, FaTelegramPlane } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { AuthProfile, useAuthData } from '../contexts/AuthData';
import { useAdminAccess } from '../contexts/AdminAccessData';
import { useSessionData } from '../contexts/SessionData';
import AdminDestinationChooser from './AdminDestinationChooser';
import ConnectedSignInMethods, { ConnectedMethodLabels } from './ConnectedSignInMethods';

declare global {
  interface Window {
    handleIfoodTelegramAuth?: (telegramPayload: Record<string, unknown>) => void;
  }
}

export type ProfileModalLabels = {
  profile: string;
  signIn: string;
  email: string;
  sendMagicLink: string;
  magicLinkSent: string;
  google: string;
  telegram: string;
  authUnavailable: string;
  name: string;
  preferredMethod: string;
  countryCode: string;
  phone: string;
  telegramUsername: string;
  language: string;
  location: string;
  address: string;
  save: string;
  logout: string;
  close: string;
  saving: string;
  adminConsole?: string;
  connectedMethods?: ConnectedMethodLabels;
  registrationConsent?: string;
  telegramMiniApp?: string;
  registrationRequired?: string;
  accountConnectionRequired?: string;
};

export const DEFAULT_PROFILE_LABELS: ProfileModalLabels = {
  profile: 'Profile', signIn: 'Sign in', email: 'Email',
  sendMagicLink: 'Send login link', magicLinkSent: 'Check your email for the login link.',
  google: 'Continue with Google', telegram: 'Continue with Telegram',
  authUnavailable: 'Authorization is temporarily unavailable', name: 'Your name',
  preferredMethod: 'Preferred contact method', countryCode: 'Country code',
  phone: 'Phone number', telegramUsername: '@username', language: 'Language',
  location: 'Location', address: 'Address', save: 'Save', logout: 'Log out',
  close: 'Close', saving: 'Saving...',
};
/**
 * ProfileAuthModal
 *
 * Owns the profile button destination for both anonymous and authenticated
 * users. It receives translated labels and app-level auth actions from
 * AuthContext, renders independent Bootstrap auth/profile states, and keeps all
 * login failures local so the menu, cart, and anonymous checkout stay usable.
 */
const ProfileAuthModal: React.FC<{
  show: boolean;
  onHide: () => void;
  labels: ProfileModalLabels;
}> = ({ show, onHide, labels }) => {
  const auth = useAuthData();
  const admin = useAdminAccess();
  const session = useSessionData();
  const navigate = useNavigate();
  const [showDestinations, setShowDestinations] = useState(false);
  const adminRef = useRef(admin);
  adminRef.current = admin;
  useEffect(() => {
    if (show && auth?.authStatus === 'authenticated') void adminRef.current?.refresh();
  }, [show, auth?.authStatus]);
  const destinations = (admin?.access?.restaurants.length ?? 0) + (admin?.access?.can_view_food_makers_status ? 1 : 0);
  const openAdmin = () => {
    if (destinations !== 1) { setShowDestinations(true); return; }
    const restaurant = admin?.access?.restaurants[0];
    const language = session?.lang ?? auth?.authUser?.lang ?? 'en';
    const path = restaurant
      ? `/MenuEditing?${new URLSearchParams({ fm: restaurant.food_maker_key, lang: language }).toString()}`
      : '/FoodMakersStatus';
    onHide();
    navigate(path);
  };
  const telegramWidgetRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState<string>('');
  const [registerAccount, setRegisterAccount] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [formProfile, setFormProfile] = useState<AuthProfile>({});
  const isAuthenticated = auth?.authStatus === 'authenticated' && Boolean(auth.authUser);
  const miniAppInitData = (window as unknown as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData;
  useEffect(() => { if (!show || !isAuthenticated) setShowDestinations(false); }, [show, isAuthenticated]);

  useEffect(() => {
    if (!show) {
      return;
    }

    let draft: AuthProfile | null = null;
    try {
      draft = JSON.parse(sessionStorage.getItem('ifoodProfileDraft:' + auth?.authUser?.user_id) ?? 'null') as AuthProfile | null;
    } catch { /* Malformed optional draft must not prevent opening profile settings. */ }
    setFormProfile({
      ...(auth?.authUser ?? {}),
      channel_first_name: auth?.authUser?.channel_first_name ?? auth?.authUser?.display_name ?? '',
      contacts: {
        ...auth?.authUser?.contacts,
        preferableMethod: auth?.authUser?.contacts?.preferableMethod ?? 'phone',
      },
      ...(draft ?? {}),
    });
  }, [auth?.authUser, show]);

  useEffect(() => {
    const widgetContainer = telegramWidgetRef.current;
    if (!show || isAuthenticated || !widgetContainer || !auth) {
      return;
    }

    widgetContainer.innerHTML = '';
    window.handleIfoodTelegramAuth = (telegramPayload: Record<string, unknown>) => {
      void auth.loginWithTelegramWidget(telegramPayload, registerAccount ? 'register' : 'login');
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', 'iFoodMeBot');
    script.setAttribute('data-size', 'medium');
    script.setAttribute('data-radius', '16');
    script.setAttribute('data-onauth', 'handleIfoodTelegramAuth(user)');
    widgetContainer.appendChild(script);

    return () => {
      widgetContainer.innerHTML = '';
    };
  }, [auth, isAuthenticated, show, registerAccount]);

  if (!auth) {
    return null;
  }

  const patchProfile = (patch: AuthProfile) => {
    setFormProfile((previous) => {
      const next = { ...previous, ...patch };
      try { sessionStorage.setItem('ifoodProfileDraft:' + auth.authUser?.user_id, JSON.stringify(next)); }
      catch { /* Storage-disabled browsers keep the in-memory draft. */ }
      return next;
    });
  };

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailStatus(null);

    try {
      await auth.requestEmailMagicLink(email, registerAccount ? 'register' : 'login');
      setEmailStatus(labels.magicLinkSent);
    } catch {
      setEmailStatus(null);
    }
  };

  const handleProfileSave = async () => {
    await auth.saveProfile({
      ...formProfile,
      contacts: {
        ...formProfile.contacts,
        preferableMethod: formProfile.contacts?.preferableMethod ?? 'phone',
        phone: formProfile.phone ? { phoneNumber: `${formProfile.countryCode ?? ''}${formProfile.phone}` } : formProfile.contacts?.phone,
        telegram: formProfile.channel_user_name
          ? { ...formProfile.contacts?.telegram, userName: formProfile.channel_user_name }
          : formProfile.contacts?.telegram,
      },
    });
    try { sessionStorage.removeItem('ifoodProfileDraft:' + auth.authUser?.user_id); }
    catch { /* Successful profile saving must not depend on browser storage. */ }
  };

  const authErrorMessage = auth.authError === 'registration_required'
    ? labels.registrationRequired ?? 'This sign-in method is new. Select the account-creation option to register.'
    : auth.authError === 'account_connection_required'
      ? labels.accountConnectionRequired ?? 'Sign in with an existing method, then connect this method in your profile.'
      : auth.authError;

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down" contentClassName="border-0 rounded-4">
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold">{isAuthenticated ? labels.profile : labels.signIn}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0" style={{ backgroundColor: '#F7F8F5' }}>
        {auth.authStatus === 'loading' && (
          <div className="d-flex align-items-center gap-2 text-secondary mb-3">
            <Spinner animation="border" size="sm" />
            <span>{labels.signIn}</span>
          </div>
        )}

        {auth.authError && (
          <Alert variant="warning" className="py-2 px-3">
            {labels.authUnavailable}: {authErrorMessage}
          </Alert>
        )}

        {!isAuthenticated ? (
          <div className="d-flex flex-column gap-3">
            <Form.Check id="auth-registration-consent" label={labels.registrationConsent ?? 'Create an account if this sign-in method is new'} checked={registerAccount} onChange={(event) => setRegisterAccount(event.target.checked)} />
            <Form onSubmit={handleEmailSubmit}>
              <Form.Group className="mb-2" controlId="profile-auth-email">
                <Form.Label className="fw-semibold">{labels.email}</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </Form.Group>
              <Button type="submit" variant="success" className="w-100 rounded-pill fw-semibold">
                {labels.sendMagicLink}
              </Button>
              {emailStatus && <div className="small text-success mt-2">{emailStatus}</div>}
            </Form>

            <Button
              type="button"
              variant="light"
              className="w-100 rounded-pill border d-flex align-items-center justify-content-center gap-2 fw-semibold"
              onClick={() => auth.startGoogleLogin(registerAccount ? 'register' : 'login')}
            >
              <FaGoogle />
              <span>{labels.google}</span>
            </Button>

            <div className="bg-white rounded-4 border p-3 text-center">
              <div className="fw-semibold mb-2 d-flex align-items-center justify-content-center gap-2">
                <FaTelegramPlane />
                <span>{labels.telegram}</span>
              </div>
              <div ref={telegramWidgetRef} className="d-inline-block" />
              {miniAppInitData && <Button type="button" variant="outline-success" className="mt-2"
                onClick={() => void auth.loginWithTelegramMiniApp(miniAppInitData, registerAccount ? 'register' : 'login')}>
                {labels.telegramMiniApp ?? 'Continue with Telegram Mini App'}
              </Button>}
            </div>
          </div>
        ) : (
          <Form className="d-flex flex-column gap-3">
            <ConnectedSignInMethods labels={labels.connectedMethods} />
            <Form.Group controlId="profile-contact-email">
              <Form.Label>{labels.email}</Form.Label>
              <Form.Control type="email" value={formProfile.email ?? ''} onChange={(event) => patchProfile({ email: event.target.value })} />
            </Form.Group>
            <Form.Group controlId="profile-name">
              <Form.Label className="fw-semibold">{labels.name}</Form.Label>
              <Form.Control
                value={formProfile.channel_first_name ?? ''}
                onChange={(event) => patchProfile({ channel_first_name: event.target.value, display_name: event.target.value })}
              />
            </Form.Group>

            <div className="row g-2">
              <div className="col-5">
                <Form.Group controlId="profile-country-code">
                  <Form.Label className="fw-semibold">{labels.countryCode}</Form.Label>
                  <Form.Select
                    value={formProfile.countryCode ?? ''}
                    onChange={(event) => patchProfile({ countryCode: event.target.value })}
                  >
                    <option value="">...</option>
                    {['+382', '+380', '+7', '+49', '+1', '+44', '+90'].map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-7">
                <Form.Group controlId="profile-phone">
                  <Form.Label className="fw-semibold">{labels.phone}</Form.Label>
                  <Form.Control
                    value={formProfile.phone ?? ''}
                    onChange={(event) => patchProfile({ phone: event.target.value.replace(/\D/g, '') })}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group controlId="profile-telegram">
              <Form.Label className="fw-semibold">{labels.telegramUsername}</Form.Label>
              <Form.Control
                value={formProfile.channel_user_name ?? formProfile.contacts?.telegram?.userName ?? ''}
                onChange={(event) => patchProfile({
                  channel_user_name: event.target.value,
                  contacts: {
                    ...formProfile.contacts,
                    telegram: { ...formProfile.contacts?.telegram, userName: event.target.value },
                  },
                })}
              />
            </Form.Group>

            <div className="row g-2">
              <div className="col-6">
                <Form.Group controlId="profile-contact-method">
                  <Form.Label className="fw-semibold">{labels.preferredMethod}</Form.Label>
                  <Form.Select
                    value={formProfile.contacts?.preferableMethod ?? 'phone'}
                    onChange={(event) => patchProfile({
                      contacts: { ...formProfile.contacts, preferableMethod: event.target.value },
                    })}
                  >
                    {['phone', 'telegram', 'whatsapp', 'viber'].map((method) => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group controlId="profile-language">
                  <Form.Label className="fw-semibold">{labels.language}</Form.Label>
                  <Form.Select
                    value={formProfile.lang ?? ''}
                    onChange={(event) => patchProfile({ lang: event.target.value })}
                  >
                    <option value="">...</option>
                    {['en', 'ru', 'ua', 'tr', 'cn'].map((lang) => (
                      <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group controlId="profile-location">
              <Form.Label className="fw-semibold">{labels.location}</Form.Label>
              <Form.Control
                value={formProfile.location ?? ''}
                onChange={(event) => patchProfile({ location: event.target.value })}
              />
            </Form.Group>

            <Form.Group controlId="profile-address">
              <Form.Label className="fw-semibold">{labels.address}</Form.Label>
              <Form.Control
                value={(formProfile as AuthProfile & { deliveryAddress?: string }).deliveryAddress ?? ''}
                onChange={(event) => patchProfile({ deliveryAddress: event.target.value } as AuthProfile)}
              />
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        {isAuthenticated ? (
          <div className="d-flex flex-column w-100 gap-2">
            {admin?.status === 'loading' && <div className="small text-secondary" role="status"><Spinner size="sm" /> Checking admin access...</div>}
            {admin?.status === 'error' && <div className="d-flex gap-2 align-items-center"><span className="small text-danger">Admin access unavailable.</span><Button size="sm" variant="outline-success" onClick={() => void admin.refresh()}>Retry</Button></div>}
            {admin?.status === 'ready' && destinations > 0 && <Button type="button" variant="outline-success" className="rounded-pill fw-bold" onClick={openAdmin}>{labels.adminConsole ?? 'Admin Console'}</Button>}
            {showDestinations && admin?.status === 'ready' && <AdminDestinationChooser onSelect={(path) => { onHide(); navigate(path); }} />}
            <div className="d-flex w-100 gap-2">
            <Button type="button" variant="light" className="rounded-pill border flex-fill" onClick={auth.logout}>
              {labels.logout}
            </Button>
            <Button type="button" variant="success" className="rounded-pill fw-bold flex-fill" onClick={handleProfileSave} disabled={auth.isProfileSaving}>
              {auth.isProfileSaving ? labels.saving : labels.save}
            </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="light" className="rounded-pill border w-100" onClick={onHide}>
            {labels.close}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};


export default ProfileAuthModal;
